import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Landmark, Copy, Check, FileDown, Printer, Save, Loader2, CreditCard } from "lucide-react";
import { toast } from "sonner";

function Fld({ label, children, full }) {
  return <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}><Label className="text-xs text-slate-500">{label}</Label>{children}</div>;
}

export default function RibCard({ s, setS, onSave }) {
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const [copied, setCopied] = useState("");
  const [saving, setSaving] = useState(false);
  const [busyPdf, setBusyPdf] = useState(false);

  const copy = async (val, which) => {
    try { await navigator.clipboard.writeText(val || ""); setCopied(which); setTimeout(() => setCopied(""), 1500); toast.success("Copié"); }
    catch { toast.error("Copie impossible"); }
  };

  const save = async () => { setSaving(true); try { await onSave(); toast.success("RIB enregistré"); } finally { setSaving(false); } };

  const getPdfBlob = async () => {
    const res = await api.get("/rib/pdf", { responseType: "blob" });
    return URL.createObjectURL(res.data);
  };
  const download = async () => {
    setBusyPdf(true);
    try { const url = await getPdfBlob(); const a = document.createElement("a"); a.href = url; a.download = "RIB-CED-Detailing-54.pdf"; a.click(); }
    catch { toast.error("Génération du PDF impossible"); } finally { setBusyPdf(false); }
  };
  const print = async () => {
    setBusyPdf(true);
    try { const url = await getPdfBlob(); const w = window.open(url); if (w) w.onload = () => w.print(); }
    catch { toast.error("Impression impossible"); } finally { setBusyPdf(false); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Landmark className="w-5 h-5 text-emerald-600" />
          <h3 className="font-heading text-base font-semibold text-slate-900">RIB / Coordonnées bancaires</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Fld label="Nom du titulaire"><Input data-testid="rib-holder" value={s.bank_holder || ""} onChange={set("bank_holder")} className="bg-slate-50 border-slate-200" /></Fld>
          <Fld label="Nom commercial"><Input data-testid="rib-commercial" value={s.bank_commercial_name || ""} onChange={set("bank_commercial_name")} className="bg-slate-50 border-slate-200" /></Fld>
          <Fld label="Nom de la banque"><Input data-testid="rib-bank" value={s.bank_name || ""} onChange={set("bank_name")} className="bg-slate-50 border-slate-200" /></Fld>
          <Fld label="Référence / intitulé (facultatif)"><Input value={s.bank_reference || ""} onChange={set("bank_reference")} className="bg-slate-50 border-slate-200" /></Fld>
          <Fld label="IBAN" full><Input data-testid="rib-iban" value={s.iban || ""} onChange={set("iban")} placeholder="FR76 ...." className="bg-slate-50 border-slate-200 font-mono" /></Fld>
          <Fld label="BIC / SWIFT"><Input data-testid="rib-bic" value={s.bic || ""} onChange={set("bic")} className="bg-slate-50 border-slate-200 font-mono" /></Fld>
          <Fld label="Adresse de la banque (facultatif)"><Input value={s.bank_address || ""} onChange={set("bank_address")} className="bg-slate-50 border-slate-200" /></Fld>
        </div>

        <div className="mt-5 space-y-3">
          <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
            <Label className="text-sm text-slate-700">Afficher automatiquement ce RIB sur les factures à régler</Label>
            <Switch checked={s.show_rib_on_invoices ?? true} onCheckedChange={(v) => setS({ ...s, show_rib_on_invoices: v })} data-testid="rib-show-invoices" />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
            <Label className="text-sm text-slate-700">Joindre automatiquement le RIB PDF aux emails de facture non payée</Label>
            <Switch checked={s.attach_rib_unpaid_emails ?? true} onCheckedChange={(v) => setS({ ...s, attach_rib_unpaid_emails: v })} data-testid="rib-attach-unpaid" />
          </div>
        </div>

        <div className="mt-5">
          <Button onClick={save} disabled={saving} data-testid="btn-save-rib" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-1.5" />}Enregistrer mon RIB
          </Button>
        </div>
      </div>

      {/* Visual preview */}
      <div className="rounded-2xl p-6 text-white relative overflow-hidden shadow-lg" style={{ background: "linear-gradient(135deg,#0f172a 0%,#0f9d6b 140%)" }} data-testid="rib-preview">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-300/90">Relevé d'Identité Bancaire</div>
            <div className="font-heading text-xl font-bold mt-1">{s.bank_commercial_name || s.commercial_name || "Mon entreprise"}</div>
          </div>
          <CreditCard className="w-8 h-8 text-white/70" />
        </div>
        <div className="mt-6 space-y-2 text-sm">
          <div className="flex justify-between gap-4"><span className="text-white/60">Titulaire</span><span className="font-medium text-right">{s.bank_holder || "—"}</span></div>
          <div className="flex justify-between gap-4"><span className="text-white/60">Banque</span><span className="font-medium text-right">{s.bank_name || "—"}</span></div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-white/60">IBAN</span>
            <span className="flex items-center gap-2"><span className="font-mono tracking-wide">{s.iban || "—"}</span>
              <button onClick={() => copy(s.iban, "iban")} data-testid="btn-copy-iban" className="p-1 rounded hover:bg-white/10">{copied === "iban" ? <Check className="w-4 h-4 text-emerald-300" /> : <Copy className="w-4 h-4 text-white/70" />}</button></span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-white/60">BIC</span>
            <span className="flex items-center gap-2"><span className="font-mono tracking-wide">{s.bic || "—"}</span>
              <button onClick={() => copy(s.bic, "bic")} data-testid="btn-copy-bic" className="p-1 rounded hover:bg-white/10">{copied === "bic" ? <Check className="w-4 h-4 text-emerald-300" /> : <Copy className="w-4 h-4 text-white/70" />}</button></span>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <Button size="sm" onClick={download} disabled={busyPdf} data-testid="btn-rib-pdf" className="bg-white/15 hover:bg-white/25 text-white border-0"><FileDown className="w-4 h-4 mr-1.5" /> Télécharger le RIB</Button>
          <Button size="sm" onClick={print} disabled={busyPdf} data-testid="btn-rib-print" className="bg-white/15 hover:bg-white/25 text-white border-0"><Printer className="w-4 h-4 mr-1.5" /> Imprimer</Button>
        </div>
      </div>
    </div>
  );
}
